/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React, { Fragment, ReactNode, useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import { updateUser } from "../../api/User";
import { SettingsHelpVideosAtom } from "../../atoms/SettingsHelpVideos";
import { UserAtom } from "../../atoms/UserAtom";
import { UserEmailArchiveAtom } from "../../atoms/UserEmailArchiveAtom";
import { IUserIRoute } from "../../generated/Api";
import useClickOutside from "../../hook/useClickOutside";
import { IOption } from "../../types/CommonTypes";
import { HelpVideoNameMap, IHelpContexts } from "../../types/HelpVideosTypes";

import useDebounce from "../../utils/UseDebounce";
import { FONT_OPTIONS } from "../Modal/container/EmailSettings/container/GeneralSettings";
import StyledAutocompleteSelect from "../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledInput from "../StyledInput";
import Switch from "../Switch";
import FavoriteTimeUserSettings from "./FavoriteTimeUserSettings";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  .divider {
    width: 100%;
    height: 1px;

    background: #eaeaea;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  display: flex;
`;

const LabelText = styled.div`
  color: #222222;
  font-weight: 400;
  font-size: 16px;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
  }
`;

const FormContent = styled.div`
  width: 100%;
  height: fit-content;

  padding: 0 14px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  grid-gap: 24px;

  .divider {
    width: 100%;
    height: 1px;
    background-color: #eaeaeabf;

    margin: 24px 0;
  }
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1140px) {
    grid-gap: 32px;
  }

  .styled-auto-complete-wrapper {
    width: fit-content;
  }

  .grid-wrapper {
    display: grid;
    grid-template-columns: 280px 1fr;
    grid-gap: 32px;

    &.center {
      align-items: center;
    }

    @media screen and (max-width: 1140px) {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      grid-gap: 16px;
    }
  }

  .flex-column-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    grid-gap: 8px;
  }

  @media screen and (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
    grid-gap: 8px;
  }
`;

const GeneralWrapperContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
`;

const GeneralWrapperWrapper = styled.div`
  width: 100%;

  padding: 40px 50px 48px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }

  @media screen and (max-width: 560px) {
    padding: 24px;
  }
`;

const InfoTooltipIcon = styled.div`
  cursor: pointer;
  margin: 3px 0px 0px 3px;
  :hover {
    color: #fb972e;
  }
`;

const InfoTooltipContent = styled.div`
  position: absolute;
  height: fit-content;
  background: white;
  z-index: 20;
  padding: 18px 14px 23px 22px;
  box-shadow: 0px 3px 20px #00000026;
  border: 1px solid #ededed;
  border-radius: 4px;
  top: 0;
  left: 25px;
  width: 360px;
`;

const InfoTitle = styled.div`
  font: normal normal normal 13px Source Sans Pro;
  color: #707070;
  margin-bottom: 8px;
`;

const InfoContent = styled.div`
  font: normal normal normal 13px Source Sans Pro;
  color: #333333;
`;

const ButtonSelect = styled.div`
  width: 150px;
  height: 42px;
  border: 1px solid #dddddd;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 5px;
  position: relative;
  border-radius: 4px;
`;

const ColorSelect = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 4px;
  cursor: pointer;
`;

const ColorPickerContainer = styled.div`
  position: absolute;
  bottom: 50px;
  right: 0;
  z-index: 20;
`;

interface GeneralWrapperProps {
  children: ReactNode;
  title: string;
  description: string;
}

function GeneralWrapper({ children, title, description }: GeneralWrapperProps) {
  return (
    <GeneralWrapperContainer>
      <GeneralWrapperWrapper>
        <TextWrapper>
          <MainTitle>{title}</MainTitle>
          <MainText>{description}</MainText>
        </TextWrapper>

        <FormContent>{children}</FormContent>
      </GeneralWrapperWrapper>
    </GeneralWrapperContainer>
  );
}

export default function EmailSettings(): JSX.Element | null {
  const [user, setUser] = useRecoilState(UserAtom);
  const { delayed, maxWidth, font } = user.emailOptions;
  const router = useRouter();
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [, setHelpVideo] = useRecoilState(SettingsHelpVideosAtom);
  const [maxWidthInput, setMaxWidthInput] = useState(maxWidth ? maxWidth : 400);
  const [limitWidth, setLimitWidth] = useState(maxWidth ? true : false);
  const [openDefaultColorPicker, setOpenDefaultColorPicker] = useState(false);
  const colorPickerRef = useClickOutside(() => {
    setOpenDefaultColorPicker(false);
  });
  const [selectFontOption, setSelectFontOption] = useState(
    FONT_OPTIONS.find((e) => e.name === user.emailOptions.font.family) ||
      FONT_OPTIONS[0]
  );
  const userEmailArchive = useRecoilValue(UserEmailArchiveAtom);
  const [fontsSize] = useState<IOption[]>([
    { name: "14px", value: "14" },
    { name: "16px", value: "16" },
    { name: "18px", value: "18" },
    { name: "20px", value: "20" },
    { name: "22px", value: "22" },
  ]);
  const [selectedFontSize, setSelectedFontSize] = useState(
    fontsSize.find((ff) => `${ff.value}` === `${font.size}`)
  );
  const debouncedMaxWidth = useDebounce(maxWidthInput, 300);

  useEffect(() => {
    (async () => {
      await updateUser(user);
    })();
  }, [user]);

  useEffect(() => {
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.SettingsGeneral],
      helpContext: IHelpContexts.SettingsGeneral,
    });
  }, []);

  useEffect(() => {
    if (
      (debouncedMaxWidth !== undefined && isNaN(debouncedMaxWidth)) ||
      (debouncedMaxWidth && debouncedMaxWidth < 400)
    ) {
      toast("Max width need to be more than 400px", {
        type: "warning",
      });
      return;
    } else {
      setValueInEmailOptions("maxWidth", debouncedMaxWidth);
    }
  }, [debouncedMaxWidth]);

  //Remove MaxWidth if switch is false
  useEffect(() => {
    if (!limitWidth) {
      let emailOptionWithoutMaxWidth = { ...user.emailOptions, maxWidth: null };

      setUser({
        ...user,
        emailOptions: emailOptionWithoutMaxWidth,
      });
    } else {
      let emailOptionWithouMaxWidth = {
        ...user.emailOptions,
        maxWidth: maxWidthInput >= 400 ? maxWidthInput : 400,
      };

      setUser({
        ...user,
        emailOptions: emailOptionWithouMaxWidth,
      });
    }
  }, [limitWidth]);

  function setValueInFontOptions(key: "family" | "size", value: any) {
    setUser({
      ...user,
      emailOptions: {
        ...user.emailOptions,
        font: {
          ...user.emailOptions.font,
          [key]: value,
        },
      },
    });
  }

  function setValueInEmailOptions(
    key: keyof IUserIRoute["emailOptions"],
    value: any
  ) {
    setUser({
      ...user,
      emailOptions: {
        ...user.emailOptions,
        [key]: value,
      },
    });
  }

  if (!user) return null;

  return (
    <Container>
      <GeneralWrapper
        title="Email"
        description="Select your email preferences."
      >
        <Fragment>
          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Split Testing</SecondaryTitle>
              <MainText>Enable split testing features</MainText>
            </div>

            <Switch
              toggled={!!user.emailOptions.splitTesting}
              whenToggled={(vle) => setValueInEmailOptions("splitTesting", vle)}
            />
          </SpaceBetween>

          {userEmailArchive && userEmailArchive.enabled && (
            <SpaceBetween>
              <div className="flex-column-wrapper">
                <SecondaryTitle>Email Archiving</SecondaryTitle>
                <MainText>Show this email on the archives</MainText>
              </div>

              <Switch
                toggled={user.emailOptions.showOnArchive}
                whenToggled={(vle) =>
                  setValueInEmailOptions("showOnArchive", vle)
                }
              />
            </SpaceBetween>
          )}

          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Email Default Styles</SecondaryTitle>
            </div>

            <Switch
              toggled={limitWidth}
              whenToggled={(vle) => setLimitWidth(vle)}
            />
          </SpaceBetween>

          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Email Width</SecondaryTitle>
              <MainText>Limit email width</MainText>
            </div>

            <div
              css={css`
                width: 110px;
              `}
            >
              <StyledInput
                error={isNaN(maxWidthInput) || maxWidthInput < 400}
                min="400"
                step="1"
                type="number"
                name="someid"
                value={maxWidthInput}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  setMaxWidthInput(value);
                }}
                disabled={!limitWidth}
              />
            </div>
          </SpaceBetween>

          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Default Font & Size</SecondaryTitle>
              <MainText>Set default font and text size</MainText>
            </div>

            <SpaceBetween
              css={css`
                color: #707070;
                width: initial;
              `}
            >
              <div
                css={css`
                  width: 170px;
                  margin-right: 15px;
                `}
              >
                <StyledAutocompleteSelect
                  forceHideNoOption
                  options={FONT_OPTIONS}
                  selectedOption={selectFontOption}
                  onOptionSelect={(option) => {
                    setValueInFontOptions("family", option.name);
                    setSelectFontOption(option);
                  }}
                  placeholder={"Type or select a font option"}
                />
              </div>
              <div
                css={css`
                  width: 100px;
                `}
              >
                <StyledAutocompleteSelect
                  forceHideNoOption
                  options={fontsSize}
                  selectedOption={selectedFontSize}
                  onOptionSelect={(option) => {
                    if (!option) {
                      option = { name: "14px", value: "14" };
                    }

                    setSelectedFontSize(option);
                    setValueInFontOptions("size", option.value);
                  }}
                  placeholder={"Type or select a font size"}
                />
              </div>
            </SpaceBetween>
          </SpaceBetween>

          <FavoriteTimeUserSettings
            user={user}
            setUser={(u: IUserIRoute) => setUser(u as any)}
          />
        </Fragment>
      </GeneralWrapper>

      <div className="divider" />

      <GeneralWrapper
        title="Link Preferences"
        description="Select your link preferences."
      >
        <Fragment>
          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>Automatically make links bold</SecondaryTitle>
            </div>

            <Switch
              toggled={!!user.emailOptions.autoBoldLinks}
              whenToggled={async (vle) => {
                setValueInEmailOptions("autoBoldLinks", vle);
              }}
            />
          </SpaceBetween>

          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>Link Color</SecondaryTitle>
            </div>

            <ButtonSelect ref={colorPickerRef}>
              <ColorSelect
                css={css`
                  background-color: ${user.emailOptions.defaultModeColorLink};
                `}
                onClick={() =>
                  setOpenDefaultColorPicker(!openDefaultColorPicker)
                }
              />
              <span style={{ marginLeft: "10%" }}>
                {user.emailOptions.defaultModeColorLink.toUpperCase()}
              </span>
              {openDefaultColorPicker && (
                <ColorPickerContainer
                  css={css`
                    .sketch-picker > .flexbox-fix:nth-child(3) > div > div {
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                    }
                  `}
                >
                  <SketchPicker
                    presetColors={[]}
                    disableAlpha
                    color={user.emailOptions.defaultModeColorLink}
                    onChangeComplete={(c) =>
                      setUser({
                        ...user,
                        emailOptions: {
                          ...user.emailOptions,
                          defaultModeColorLink: c.hex,
                        },
                      })
                    }
                  />
                </ColorPickerContainer>
              )}
            </ButtonSelect>
          </SpaceBetween>

          {/* <SpaceBetween
            style={{ marginBottom: "20px" }}
            id="automatic-stats-email"
          >
            <div className="grid-wrapper">
              <SecondaryTitle>
                Automatic Interest Tagging
                <div style={{ position: "relative", marginLeft: "6px" }}>
                  <InfoTooltipIcon
                    onMouseEnter={() => setShowInfoTooltip(true)}
                    onMouseLeave={() => setShowInfoTooltip(false)}
                  >
                    <AiOutlineInfoCircle color="#BBBBBB" />
                  </InfoTooltipIcon>
                  {showInfoTooltip && (
                    <InfoTooltipContent>
                      <InfoTitle>AUTOMATIC INTEREST TAGGING</InfoTitle>
                      <InfoContent>
                        Automated interest tagging smartly remembers frequently
                        used URLS and the tags you want to assign to them when
                        people click on them to give you a faster user
                        experience and more consistent data for click tracking.
                      </InfoContent>
                    </InfoTooltipContent>
                  )}
                </div>
              </SecondaryTitle>
            </div>

            <Switch
              toggled={!!user.options?.statsEmail?.receiveDailyEmail}
              whenToggled={async (vle) => {}}
            />
          </SpaceBetween> */}
        </Fragment>
      </GeneralWrapper>
    </Container>
  );
}
